import { BlogPostContentType } from 'constants/blog';
import { BLOG_CASE_STUDY_OWWN_COVER } from 'constants/images';
import { BlogPost } from 'containers/blog/post/types';

const CaseStudyOwwn: BlogPost = {
  id: 'case-study-deinestudienfinanzierung',
  title: 'Case study: deineStudienfinanzierung',
  subtitle: 'Rewriting a web platform with flexible and scalable architecture.',
  previewImage: BLOG_CASE_STUDY_OWWN_COVER,
  date: new Date('25 May 2024'),
  content: [
    {
      type: BlogPostContentType.TITLE,
      content: 'Case study: deineStudienfinanzierung',
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: 'Rewriting a web platform with flexible and scalable architecture.',
    },
    {
      type: BlogPostContentType.POST_INFO,
      content: '25 May 2024 · 5 minute read',
    },
    {
      type: BlogPostContentType.MEDIUM_IMAGE,
      content: BLOG_CASE_STUDY_OWWN_COVER,
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Client: </span>,
        `deineStudienfinanzierung, a web platform for student financing, existing for 5 years.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Challenge: </span>,
        `Rewrite the platform due to outdated technologies and changing product scope.
      Desired a lean, state-of-the-art platform focused on main features.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Solution: </span>,
        `Opted for experienced freelancers for flexibility reasons. Chose my agency for its
      full-stack development expertise, with a focus on backend and infrastructure. Impressed by
      the agency's approach to problem-solving and technical solutions.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Process: </span>,
        `Already had a clear vision of what was needed. Initial focus on foundational work
      using tools like NextJS, Apollo, React, and Prisma. Built upon this foundation to develop
      APIs, backend logic, and UI.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Communication: </span>,
        `Utilised tools like Slack, Meet, Jira, and GitHub. Adopted a minimal
      meeting strategy with daily standups for effective remote collaboration.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Results: </span>,
        `MVP launched within 3 months, with successful user migration from the old
      platform. Platform performed well from day one with thousands of initial users and minimal
      issues.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Cost Efficiency: </span>,
        `Low infrastructure costs. The platform is scalable with minimal technical
      barriers.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Coding Experience: </span>,
        `The foundation built was robust, making it easy to develop more
      complex flows.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Client&apos;s Advice: </span>,
        `Finding the right team with specific expertise in both backend and frontend
      development is important. Trusted in the team's decision-making and proactive approach.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: [
        <span style={{ fontWeight: 'bold' }}>Recommendation: </span>,
        `The client would recommend us for our competence, confidence, and
      communication skills. They appreciated the well-structured approach, proactive problem-
      solving, and technical expertise.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TITLE,
      content: 'Case Study: StudyFinancing',
    },
    {
      type: BlogPostContentType.TEXT,
      content: `This is a case study with Marcus, the lead developer at deineStudienfinanzierung, that we
      helped rebuild the main product of the company. Here's a dialogue on how we worked
      together launching their MVP in a few months and scaling to thousands of users afterwards.`,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.SECTION_BREAK,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>
          Can you tell us about your company, StudyFinancing?
        </span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        `We are a `,
        <span style={{ fontWeight: 'bold' }}>web platform</span>,
        ` for students to explore financing options for their studies.
      Our main focus is on providing an online application flow for the student loan of KfW,
      a German state-owned investment and development bank. Over our `,
        <span style={{ fontWeight: 'bold' }}>5-year existence</span>,
        `, we've evolved, adding and removing features. We recognized that we
      need to update due to deprecated technologies and changes in our product scope,
      and decided on a `,
        <span style={{ fontWeight: 'bold' }}>complete rewrite</span>,
        ` of our platform, aiming for a leaner, more
      focused product.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>Why did you choose to work with our agency?</span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        `We wanted to achieve budget flexibility, which led us to seek freelancers, and you
      stood out during our search. We were impressed by your focus on `,
        <span style={{ fontWeight: 'bold' }}>backend and infrastructure</span>,
        ` and your approach to using `,
        <span style={{ fontWeight: 'bold' }}>modern technologies</span>,
        `. As we wanted
      two freelancers, we were looking for a good combination and you really seemed to
      be a good fit. The decision was clear after our initial discussions, where you made a
      good impression when we talked and demonstrated `,
        <span style={{ fontWeight: 'bold' }}>impressive technical understanding</span>,
        ` and a clear vision of how to achieve our goals.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>
          Back when we started, all we had was a diagram and the question “how would you build
          this?”. How would you describe the process from 0 to the MVP?
        </span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        `Having an established product meant we knew what we needed, but we wanted to
      have a clean view on things, so that we don’t just reimplement what we already had,
      but that we really make sure we think about it again and look into the best practices
      for the solution. We focused initially on foundational work, establishing libraries,
      frameworks, and tools, including `,
        <span style={{ fontWeight: 'bold' }}>NextJS, Apollo, React</span>,
        `, and `,
        <span style={{ fontWeight: 'bold' }}>Prisma</span>,
        `. The early
      weeks were spent setting up the `,
        <span style={{ fontWeight: 'bold' }}>database</span>,
        `, app, and storage structures. Once the
      foundation was laid, we moved to developing the `,
        <span style={{ fontWeight: 'bold' }}>APIs, backend logic</span>,
        `, and `,
        <span style={{ fontWeight: 'bold' }}>UI</span>,
        `,
      integrating `,
        <span style={{ fontWeight: 'bold' }}>third-party APIs</span>,
        ` for data handling and working closely with your team to
        `,
        <span style={{ fontWeight: 'bold' }}>incrementally complete the flow</span>,
        `.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>
          How was communication managed in a fully remote setting?
        </span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        `We utilized `,
        <span style={{ fontWeight: 'bold' }}>Slack, Meet, Jira</span>,
        `, and `,
        <span style={{ fontWeight: 'bold' }}>GitHub</span>,
        ` for communication and project
      management. Our initial phase included more detailed meetings for decision-making,
      then we transitioned to `,
        <span style={{ fontWeight: 'bold' }}>daily standups</span>,
        ` for quick sync-ups but we really tried to keep
      the meetings at a minimum so that we can all focus on developing. I think that
      worked very well, we managed to clarify most of the things though github, code
      reviews, through jira ticket comments, and, if necessary, Slack. But we barely had
      additional meeting on top. That made it possible that `,
        <span style={{ fontWeight: 'bold' }}>everybody worked in their own schedule</span>,
        `. We made sure we had touchpoints, that we were available, but the
      work setup allowed everybody to work in their hours.`,
      ],
    },
    {
      type: BlogPostContentType.TEXT,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: <span style={{ fontWeight: 'bold' }}>What was the weekly meeting time?</span>,
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        `We had 15 minutes standups, which mostly were not that long, and from time to time
      we had more things to discuss, but not more than 1 or 2 hours on average a week.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>
          How did the product scale after launching the MVP?
        </span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        ``,
        <span style={{ fontWeight: 'bold' }}>It worked well from day 1</span>,
        `. We went live in the beginning of a critical phase, when
      many students come to our platform to submit their semester study certificates, which
      is a regular process that students have to do every half year. We were expecting
      relatively low traffic so that we can monitor that everything goes well, and that we
      could also switch back to the old platform if necessary, but we never had to use it.
      The user base is steadily growing. `,
        <span style={{ fontWeight: 'bold' }}>
          The platform has been stably running since day 1
        </span>,
        `.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>
          As a developer yourself, how do you find the coding experience on the new platform?
        </span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        ``,
        <span style={{ fontWeight: 'bold' }}>
          It’s very good. I am super happy with how well built the foundation is
        </span>,
        `. I felt like
      it’s more or less plug and play, to bring all the pieces we already had together. It’s
      really easy to build more complex flows now based on the foundation we built.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>
          What advice do you have for others looking for a software agency?
        </span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        `We tried to find a good match in the beginning. We knew exactly what we needed
      and what we wanted to achieve, and we had a clear view on what external resources
      we need. It was a pretty good decision to not have just generic developers, but
      experts in those 2 specific fields (backend and frontend), that are decision makers. `,
        <span style={{ fontWeight: 'bold' }}>
          I really had the confidence to trust in you to make the right decision
        </span>,
        `. The
      confidence you give, your ideas and suggestions made me feel this is something I
      could trust.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: ``,
    },
    {
      type: BlogPostContentType.TEXT,
      content: (
        <span style={{ fontWeight: 'bold' }}>Why would you recommend us to other customers?</span>
      ),
    },
    {
      type: BlogPostContentType.TEXT_LEFT_BORDER,
      content: [
        ``,
        <span style={{ fontWeight: 'bold' }}>Competence, confidence, communication skills</span>,
        `, it was always very clear to
      communicate with you. `,
        <span style={{ fontWeight: 'bold' }}>You are very well structured</span>,
        `, you made sure whatever we
      discussed was properly documented in Jira, Github. It was very nice for me, because
      I didn’t feel like I had to manage the project and you would just do the tasks I assign
      to you, but `,
        <span style={{ fontWeight: 'bold' }}>
          you really figured out ways of what you should do, how you should do it
        </span>,
        ` and this was something I was really happy about. It was not my sole
      responsibility of knowing what to do and how to do it. `,
        <span style={{ fontWeight: 'bold' }}>
          You were proactive to do those things, which I really liked
        </span>,
        `. From a tech perspective, you proved that you really know, you have a lot of experience how to set up things, how to scale things,
      and to make the right decision. `,
        <span style={{ fontWeight: 'bold' }}>
          I wouldn’t doubt any technical decision that we have made in the last months
        </span>,
        `.`,
      ],
    },
    {
      type: BlogPostContentType.SUBTITLE,
    },
    {
      type: BlogPostContentType.CONTACT_ME_FORM,
    },
  ],
};

export default CaseStudyOwwn;
